import type { PushSubscriptionPayload } from '../../shared/types';

/**
 * Converts a base64 or base64url encoded VAPID public key string into a Uint8Array
 * suitable for `pushManager.subscribe({ applicationServerKey })`.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Serializes a browser PushSubscription into the DTO shape expected by the API.
 */
export function serializePushSubscription(
  sub: PushSubscription,
  userAgent?: string,
): PushSubscriptionPayload {
  const p256dh = arrayBufferToBase64(sub.getKey('p256dh'));
  const auth = arrayBufferToBase64(sub.getKey('auth'));

  return {
    endpoint: sub.endpoint,
    keys: { p256dh, auth },
    ...(userAgent ? { userAgent } : {}),
  };
}

/**
 * Global signal incremented or dispatched when the active Service Worker
 * sends a NEW_EPISODE_PUSH message to active client tabs.
 */
export const latestEpisodePush = {
  value: null as { episodeId?: number; feedId?: number; url?: string } | null,
};

export function initPushMessageListener(): () => void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return () => {};
  }

  const handleMessage = (event: MessageEvent) => {
    if (event.data?.type === 'NEW_EPISODE_PUSH') {
      latestEpisodePush.value = event.data.data ?? {};
      window.dispatchEvent(new CustomEvent('podcastie:new-episode', { detail: event.data.data }));
    }
  };

  navigator.serviceWorker.addEventListener('message', handleMessage);
  return () => {
    navigator.serviceWorker.removeEventListener('message', handleMessage);
  };
}

